import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useOwnerQuery } from '@/hooks/useOwnerSettings'
import { addMonths, addYears, format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Link } from 'react-router-dom'

const LABELS: Record<
  'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM',
  string
> = {
  MONTHLY_STANDARD: 'Mensuel (Standard)',
  YEARLY_ECO: 'Annuel (Éco)',
  LIFETIME_PREMIUM: 'Annuel (Premium)',
}

function fmt(d: Date | null) {
  if (!d) return '—'
  try {
    return format(d, 'dd MMM yyyy', { locale: fr })
  } catch {
    return d.toLocaleDateString('fr-FR')
  }
}

function computeNextRenewal(
  plan: 'MONTHLY_STANDARD' | 'YEARLY_ECO' | 'LIFETIME_PREMIUM',
  startedAt: string | null
): { label: string; date: Date | null } {
  if (!startedAt) return { label: 'Aucun plan actif', date: null }
  const start = new Date(startedAt)

  if (plan === 'MONTHLY_STANDARD') {
    return { label: 'Renouvellement', date: addMonths(start, 1) }
  }
  if (plan === 'YEARLY_ECO') {
    return { label: 'Renouvellement', date: addYears(start, 1) }
  }
  // LIFETIME_PREMIUM : renouvellement annuel pour les nouveaux achats,
  // "à vie" par défaut pour les comptes historiques sans date connue.
  return { label: 'Renouvellement', date: addYears(start, 1) }
}

export default function PlanInfoCard() {
  const { data: owner, status } = useOwnerQuery(true)

  if (status === 'pending') {
    return (
      <Card>
        <CardHeader>
          <h2 className='text-xl font-semibold'>Plan & renouvellement</h2>
          <p className='text-sm text-muted-foreground'>Chargement…</p>
        </CardHeader>
      </Card>
    )
  }

  if (!owner) return null

  const hasPlan = !!owner.planStartedAt
  const startAt = owner.planStartedAt ? new Date(owner.planStartedAt) : null
  const fallbackNext = computeNextRenewal(
    owner.plan,
    owner.planStartedAt ?? null
  )
  const isLegacyLifetime =
    owner.plan === 'LIFETIME_PREMIUM' && !owner.nextRenewalAt
  const nextInfo = isLegacyLifetime
    ? { label: 'Durée', date: null }
    : owner.nextRenewalAt
    ? { label: fallbackNext.label, date: new Date(owner.nextRenewalAt) }
    : fallbackNext

  const trialActive =
    !hasPlan && owner.trialStartedAt && owner.trialEndsAt
      ? new Date(owner.trialEndsAt) > new Date()
      : false

  return (
    <Card>
      <CardHeader className='flex flex-col gap-1'>
        <h2 className='text-xl font-semibold'>Plan & renouvellement</h2>
        <p className='text-sm text-muted-foreground'>
          Consultez votre offre actuelle et l’échéance.
        </p>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Bandeau principal */}
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3'>
          <div className='flex items-center gap-3'>
            <Badge variant={hasPlan ? 'default' : 'outline'}>
              {hasPlan ? 'Plan actif' : 'Aucun plan'}
            </Badge>
            <div className='text-sm'>
              {hasPlan ? (
                <span className='font-medium'>
                  {LABELS[owner.plan]}{' '}
                  <span className='text-muted-foreground'>
                    (démarré le {fmt(startAt)})
                  </span>
                </span>
              ) : trialActive ? (
                <span className='font-medium text-amber-700'>
                  Essai en cours — se termine le{' '}
                  {fmt(new Date(owner.trialEndsAt!))}
                </span>
              ) : owner.trialEndsAt ? (
                <span className='font-medium text-rose-700'>
                  Essai expiré le {fmt(new Date(owner.trialEndsAt))}
                </span>
              ) : (
                <span className='text-muted-foreground'>Aucun plan actif</span>
              )}
            </div>
          </div>

          <Link to='/pricing'>
            <Button variant='default'>Gérer mon plan</Button>
          </Link>
        </div>

        {/* Détails de renouvellement */}
        {hasPlan ? (
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
            <div className='rounded-lg border bg-background p-3'>
              <div className='text-xs text-muted-foreground'>Plan</div>
              <div className='mt-1 font-medium'>{LABELS[owner.plan]}</div>
            </div>
            <div className='rounded-lg border bg-background p-3'>
              <div className='text-xs text-muted-foreground'>Début</div>
              <div className='mt-1 font-medium'>{fmt(startAt)}</div>
            </div>
            <div className='rounded-lg border bg-background p-3'>
              <div className='text-xs text-muted-foreground'>
                {nextInfo.label}
              </div>
              <div className='mt-1 font-medium'>
                {isLegacyLifetime ? 'À vie' : fmt(nextInfo.date)}
              </div>
            </div>
          </div>
        ) : (
          <div className='rounded-lg border bg-background p-3 text-sm text-muted-foreground'>
            {trialActive
              ? 'Votre essai est actif. Activez un plan pour conserver vos relances et quotas.'
              : 'Aucun plan actif. Choisissez une offre pour continuer sans limites.'}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
